import React, { useEffect, useState } from 'react';
import Typography from '@mui/material/Typography';
import { Box, Button, Snackbar, Alert } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const EQATRedirectionContainer: React.FC = () => {

  const [validUrl, setValidUrl] = useState<boolean | null>(false);
  const [copySuccess, setCopySuccess] = useState<boolean>(false);

  const copyUrlToClipboard = async () => {
    const url = window.location.href;
    
    // Try modern Clipboard API first
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(url);
        setCopySuccess(true);
        return;
      } catch (err) {
        console.warn('Clipboard API failed, trying fallback: ', err);
      }
    }
    
    // Fallback for older browsers or non-secure contexts
    try {
      const textArea = document.createElement('textarea');
      textArea.value = url;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        setCopySuccess(true);
      } else {
        throw new Error('execCommand copy failed');
      }
    } catch (err) {
      console.error('Failed to copy URL: ', err);
      alert('Failed to copy URL. Please copy it manually: ' + url);
    }
  };

  const handleCloseSnackbar = () => {
    setCopySuccess(false);
  };

  useEffect(() => {

    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");

    if (code && state) {
      // window.location.href = redirectionUrl + `?code=${code}&state=${state}`;
      setValidUrl(true);
    } else {
      setValidUrl(false);
    }

  }, []);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        boxSizing: 'border-box'
      }}
    >
      {validUrl === true && (
        <>
          <CheckCircleIcon
            sx={{
              fontSize: '80px',
              color: '#27ae60',
              marginBottom: '24px'
            }}
          />

          <Typography
            variant="h5"
            sx={{
              fontWeight: 'bold',
              color: '#333333',
              textAlign: 'center',
              marginBottom: '16px',
              fontSize: '24px'
            }}
          >
            Your call is authenticated.
          </Typography>

          {/* Instructional Text */}
          <Typography
            variant="body1"
            sx={{
              color: '#333333',
              textAlign: 'center',
              fontSize: '16px',
              lineHeight: '1.5',
              maxWidth: '400px',
              marginBottom: '24px'
            }}
          >
            Please copy the URL and paste it into your EQAT application to proceed.
          </Typography>

          <Button
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            onClick={copyUrlToClipboard}
            sx={{
              borderColor: '#27ae60',
              color: '#27ae60',
              '&:hover': {
                borderColor: '#1e8449',
                backgroundColor: 'rgba(39, 174, 96, 0.04)'
              }
            }}
          >
            Copy URL
          </Button>
        </>
      )}
      {validUrl === false && (
        <>
          <ErrorIcon
            sx={{
              fontSize: '80px',
              color: '#e74c3c',
              marginBottom: '24px'
            }}
          />

          <Typography
            variant="h5"
            sx={{
              fontWeight: 'bold',
              color: '#333333',
              textAlign: 'center',
              marginBottom: '16px',
              fontSize: '24px'
            }}
          >
            Authentication failed.
          </Typography>

          {/* Error Message */}
          <Typography
            variant="body1"
            sx={{
              color: '#333333',
              textAlign: 'center',
              fontSize: '16px',
              lineHeight: '1.5',
              maxWidth: '400px',
              marginBottom: '24px'
            }}
          >
            The authentication request failed.Please try again or contact support if the issue persists.
          </Typography>

          <Button
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            onClick={copyUrlToClipboard}
            sx={{
              borderColor: '#e74c3c',
              color: '#e74c3c',
              '&:hover': {
                borderColor: '#c0392b',
                backgroundColor: 'rgba(231, 76, 60, 0.04)'
              }
            }}
          >
            Copy URL
          </Button>
        </>
      )}

      <Snackbar
        open={copySuccess}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          URL copied to clipboard!
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default EQATRedirectionContainer;